import React, { useState } from "react";
import { Lobby } from "../../../../services/api/LobbyApi";
import { api } from "../../../../services/api";
import LobbySettings from "./LobbySettings";
import styles from "./HostControls.module.scss";

interface HostControlsProps {
  lobby: Lobby;
  refresh: () => void;
}

const HostControls: React.FC<HostControlsProps> = ({ lobby, refresh }) => {
  const [showSettings, setShowSettings] = useState(false);
  const [loading, setLoading] = useState<string | null>(null);

  const canEdit = lobby.state === "OPEN";
  const canStart = lobby.canStart;
  const canCancel = lobby.state !== "COMPLETED" && lobby.state !== "CANCELLED";
  const canEnd = lobby.state === "ACTIVE" || lobby.state === "PREPARING";
  const canLock = lobby.state === "OPEN";

  const handleAction = async (action: string, fn: () => Promise<any>) => {
    setLoading(action);
    try {
      await fn();
      refresh();
    } catch (err) {
      console.error(`${action} failed:`, err);
    } finally {
      setLoading(null);
    }
  };

  const handleKick = (targetUserId: string) => {
    handleAction("kick", () =>
      api.lobby.kickPlayer(lobby.lobbyId, targetUserId)
    );
  };

  const handleTransferHost = (targetUserId: string) => {
    handleAction("transfer", () =>
      api.lobby.transferHost(lobby.lobbyId, targetUserId)
    );
  };

  return (
    <div className={styles.hostControls}>
      <div className={styles.actions}>
        {canStart && (
          <button
            className={`${styles.button} ${styles.startButton}`}
            onClick={() =>
              handleAction("start", () =>
                api.lobby.startMatch(lobby.lobbyId)
              )
            }
            disabled={loading === "start"}
          >
            {loading === "start" ? "Starting..." : "Start Match"}
          </button>
        )}

        {canLock && (
          <button
            className={styles.button}
            onClick={() =>
              handleAction("lock", () =>
                lobby.locked
                  ? api.lobby.unlockLobby(lobby.lobbyId)
                  : api.lobby.lockLobby(lobby.lobbyId)
              )
            }
            disabled={loading === "lock"}
          >
            {lobby.locked ? "Unlock" : "Lock"}
          </button>
        )}

        {canEdit && (
          <button
            className={styles.button}
            onClick={() => setShowSettings(true)}
          >
            Edit Settings
          </button>
        )}

        {canCancel && (
          <button
            className={`${styles.button} ${styles.cancelButton}`}
            onClick={() =>
              handleAction("cancel", () =>
                api.lobby.cancelLobby(lobby.lobbyId)
              )
            }
            disabled={loading === "cancel"}
          >
            {loading === "cancel" ? "Cancelling..." : "Cancel Lobby"}
          </button>
        )}

        {canEnd && (
          <button
            className={`${styles.button} ${styles.cancelButton}`}
            onClick={() =>
              handleAction("end", () =>
                api.lobby.completeMatch(lobby.lobbyId)
              )
            }
            disabled={loading === "end"}
          >
            {loading === "end" ? "Ending..." : "End Session"}
          </button>
        )}
      </div>

      {showSettings && (
        <LobbySettings
          lobby={lobby}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
};

export default HostControls;
